import { useCallback, useEffect, useRef, useState } from "react";

// Tiny SWR-style cache. Returning to a page that already loaded renders the
// cached data instantly, then revalidates in the background. In-flight requests
// for the same key are de-duplicated so two components mounting at once share
// one network call.
const _cache = new Map(); // key -> last successful result
const _inflight = new Map(); // key -> Promise

export function useCachedResource(key, fetcher) {
  const [data, setData] = useState(() => _cache.get(key));
  const [loading, setLoading] = useState(() => !_cache.has(key));
  const [error, setError] = useState(null);

  // Keep the latest fetcher without making it a dependency (callers usually
  // pass a fresh arrow function each render).
  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;

  const run = useCallback(
    async (force = false) => {
      try {
        setError(null);
        if (!_cache.has(key)) setLoading(true);
        let promise = force ? undefined : _inflight.get(key);
        if (!promise) {
          promise = Promise.resolve().then(() => fetcherRef.current());
          _inflight.set(key, promise);
        }
        const result = await promise;
        _cache.set(key, result);
        setData(result);
        return result;
      } catch (e) {
        setError(e);
        throw e;
      } finally {
        _inflight.delete(key);
        setLoading(false);
      }
    },
    [key]
  );

  useEffect(() => {
    setData(_cache.get(key));
    setLoading(!_cache.has(key));
    run().catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  const refresh = useCallback(() => {
    _inflight.delete(key);
    return run(true).catch(() => {});
  }, [key, run]);

  return { data, loading, error, refresh };
}

export function invalidateCache(key) {
  _cache.delete(key);
  _inflight.delete(key);
}

export function mutateCache(key, value) {
  _cache.set(key, value);
}
